import {
  getSortedBreakpointsKeys,
  sortByBreakpointValue,
  subtractForMediaQueryMaxWidth,
  toMediaQueryString,
} from './mediaQueryUtils';

export type BreakpointDetail = {
  breakpoint: string;
  minWidth: string;
  maxWidth?: string;
  maxWidthQuery: string;
  minWidthQuery: string;
  minMaxQuery: string;
};

export type BreakpointsDetails = {
  keys: string[];
  breakpoints: BreakpointDetail[];
};

/**
 * Get breakpoints details
 *
 * Takes an object of breakpoint definitions and returns the breakpoints keys
 * and helpful information on each breakpoint that can be used for generating
 * eg. media queries with min / max values etc..
 *
 * @example
 *   const breakpoints = getBreakpointsDetails({ sm: '400px', md: '800px' });
 *   console.log(breakpoints.keys); // ['base', 'sm', 'md];
 *   console.log(breakpoints.breakpoints.sm);
 *   // { breakpoint: 'sm', minWidth: '400px', maxWidth: '799px', minMaxQuery: '(min-width: 400px) and (max-width: 799px)' ...}
 */
export function getBreakpointsDetails(
  breakpoints?: Record<string, string>,
): BreakpointsDetails {
  if (!breakpoints) {
    return { keys: [], breakpoints: [] };
  }

  const enrichedBreakpoints = { base: '0px', ...breakpoints };

  const detailedBreakpoints = Object.entries(enrichedBreakpoints)
    .sort(sortByBreakpointValue)
    .map(([breakpoint, minWidth], index, entries) => {
      const nextBreakpoint = entries[index + 1] ?? [];
      const nextBreakpointValue = nextBreakpoint[1];
      const maxWidth =
        parseFloat(nextBreakpointValue) > 0
          ? subtractForMediaQueryMaxWidth(nextBreakpointValue)
          : undefined;

      return {
        breakpoint,
        minWidth,
        maxWidth,
        maxWidthQuery: toMediaQueryString(null, maxWidth),
        minWidthQuery: toMediaQueryString(minWidth),
        minMaxQuery: toMediaQueryString(minWidth, maxWidth),
      };
    });

  return {
    keys: getSortedBreakpointsKeys(enrichedBreakpoints),
    breakpoints: detailedBreakpoints,
  };
}
